import { useState } from 'react'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  Check,
  X,
  Clock,
  Building2,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'
import type { superAdmin } from '@repo/shared'

type Alert = superAdmin.Alert

interface AlertListProps {
  alerts: Alert[]
  loading?: boolean
  onAcknowledge: (id: string) => void
  onResolve: (id: string, resolution: string) => void
  onDismiss: (id: string) => void
}

export function AlertList({ alerts, loading = false, onAcknowledge, onResolve, onDismiss }: AlertListProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [resolutionText, setResolutionText] = useState('')

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical':
        return <AlertCircle className="w-5 h-5 text-red-500" />
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />
      default:
        return <Info className="w-5 h-5 text-blue-500" />
    }
  }

  const getSeverityBadge = (severity: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      critical: { label: 'Crítico', className: 'bg-red-100 text-red-700 border-red-200' },
      warning: { label: 'Alerta', className: 'bg-amber-100 text-amber-700 border-amber-200' },
      info: { label: 'Info', className: 'bg-blue-100 text-blue-700 border-blue-200' },
    }
    const badge = badges[severity] ?? badges.info!
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${badge.className}`}>
        {badge.label}
      </span>
    )
  }

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { label: string; className: string }> = {
      active: { label: 'Ativo', className: 'bg-red-100 text-red-700' },
      acknowledged: { label: 'Reconhecido', className: 'bg-amber-100 text-amber-700' },
      resolved: { label: 'Resolvido', className: 'bg-green-100 text-green-700' },
      dismissed: { label: 'Descartado', className: 'bg-gray-100 text-gray-700' },
    }
    const badge = badges[status] ?? badges.active!
    return (
      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.className}`}>
        {badge.label}
      </span>
    )
  }

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMs / 3600000)
    const diffDays = Math.floor(diffMs / 86400000)

    if (diffMins < 60) {
      return `há ${diffMins} min`
    } else if (diffHours < 24) {
      return `há ${diffHours}h`
    } else if (diffDays < 7) {
      return `há ${diffDays}d`
    }
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
    })
  }

  const handleResolve = (id: string) => {
    onResolve(id, resolutionText)
    setResolutionText('')
    setExpandedId(null)
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (alerts.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Check className="w-8 h-8 text-green-600" />
        </div>
        <p className="text-gray-500 font-medium">Nenhum alerta encontrado</p>
        <p className="text-sm text-gray-400 mt-1">Todos os alertas foram resolvidos ou descartados</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {alerts.map((alert) => (
        <div
          key={alert.id}
          className={`bg-white rounded-xl border transition-all ${
            alert.status === 'active'
              ? alert.severity === 'critical'
                ? 'border-red-200 shadow-sm shadow-red-100'
                : 'border-amber-200'
              : 'border-gray-200'
          }`}
        >
          <div className="p-4">
            <div className="flex items-start gap-4">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  alert.severity === 'critical'
                    ? 'bg-red-100'
                    : alert.severity === 'warning'
                      ? 'bg-amber-100'
                      : 'bg-blue-100'
                }`}
              >
                {getSeverityIcon(alert.severity)}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium text-gray-900">{alert.title}</h3>
                  {getSeverityBadge(alert.severity)}
                  {getStatusBadge(alert.status)}
                </div>

                <p className="text-sm text-gray-600 mt-1">{alert.message}</p>

                <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" />
                    {formatDate(alert.created_at)}
                  </div>
                  {alert.organization_name && (
                    <div className="flex items-center gap-1">
                      <Building2 className="w-3.5 h-3.5" />
                      {alert.organization_name}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2">
                {alert.status === 'active' && (
                  <>
                    <button
                      onClick={() => onAcknowledge(alert.id)}
                      className="px-3 py-1.5 text-sm bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors"
                    >
                      Reconhecer
                    </button>
                    <button
                      onClick={() => setExpandedId(expandedId === alert.id ? null : alert.id)}
                      className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center gap-1"
                    >
                      Resolver
                      {expandedId === alert.id ? (
                        <ChevronUp className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </button>
                  </>
                )}
                {alert.status === 'acknowledged' && (
                  <button
                    onClick={() => setExpandedId(expandedId === alert.id ? null : alert.id)}
                    className="px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors flex items-center gap-1"
                  >
                    Resolver
                    {expandedId === alert.id ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                )}
                {(alert.status === 'active' || alert.status === 'acknowledged') && (
                  <button
                    onClick={() => onDismiss(alert.id)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Descartar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Expanded resolution form */}
          {expandedId === alert.id && (
            <div className="border-t border-gray-100 p-4 bg-gray-50 rounded-b-xl">
              <div className="flex gap-3">
                <input
                  type="text"
                  value={resolutionText}
                  onChange={(e) => setResolutionText(e.target.value)}
                  placeholder="Descreva como o problema foi resolvido..."
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                />
                <button
                  onClick={() => handleResolve(alert.id)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm"
                >
                  Confirmar Resolução
                </button>
              </div>
            </div>
          )}

          {/* Resolution notes for resolved alerts */}
          {alert.status === 'resolved' && alert.resolution_notes && (
            <div className="border-t border-gray-100 p-4 bg-green-50 rounded-b-xl">
              <p className="text-sm text-green-700">
                <span className="font-medium">Resolução:</span> {alert.resolution_notes}
              </p>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}
